/* eslint-disable react/no-danger */
import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Collapse, Button, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faFileCode } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import FormSelectDevType from "../../../../components/form/FormSelectDevType";
import FormSelectDevTypeNames from "../../../../components/form/FormSelectDevTypeNames";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import FormSelectMessage from "../../../../components/form/FormSelectMessage";
import FormSelectBank from "../../../../components/form/FormSelectBank";
import FormSelectLift from "../../../../components/form/FormSelectLift";
// INTERFACES
import {
	AdapterCdoSequentInterface,
	DiscreteToLn2EntryInterface,
} from "../../../../../interfaces/AdapterCdoSequent.interface";
import { RootState } from "../../../../../app/store";

export default function CdoSequentPage({
	setCdo,
	cdoValue,
}: {
	setCdo: (newValue: AdapterCdoSequentInterface) => void;
	cdoValue: AdapterCdoSequentInterface;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { theme } = useSelector((state: RootState) => state.home);
	const { Panel } = Collapse;
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [discreteMessage, setDiscreteMessage] =
		useState<DiscreteToLn2EntryInterface>({
			key: {
				read_response_message: {
					board_address: 0,
					pin_num: 0,
					value: true,
				},
			},
			value: {
				message: {
					msg_id: "",
					active: true,
					position: 0,
				},
				lift_id: {
					id: "",
					name: "",
				},
				bank_id: {
					id: "",
					name: "",
				},
				dev_type: 0,
			},
		});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeSelect = (newValue: number) => {
		const copy = structuredClone(cdoValue);
		copy.dev_type = newValue;
		setCdo(copy);
	};

	const onChangeSelectMessageDevType = (newValue: number) => {
		const copy = structuredClone(discreteMessage);
		copy.value.dev_type = newValue;
		setDiscreteMessage(copy);
	};

	const formatJsonWithColors = (obj: any) => {
		const json = JSON.stringify(obj, null, 4)
			.replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
			.replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
			.replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
		return json;
	};

	const onChangeFormValue = (
		newValue: string | number | boolean,
		variableName: string,
	) => {
		const copy: DiscreteToLn2EntryInterface = structuredClone(discreteMessage);
		if (
			variableName === "board_address" &&
			typeof newValue === "number" &&
			copy.key.read_response_message
		) {
			copy.key.read_response_message.board_address = newValue;
		} else if (
			variableName === "pin_num" &&
			typeof newValue === "number" &&
			copy.key.read_response_message
		) {
			copy.key.read_response_message.pin_num = newValue;
		} else if (
			variableName === "value" &&
			typeof newValue === "boolean" &&
			copy.key.read_response_message
		) {
			copy.key.read_response_message.value = newValue;
		} else if (variableName === "active" && typeof newValue === "boolean") {
			copy.value.message.active = newValue;
		} else if (variableName === "position" && typeof newValue === "number") {
			copy.value.message.position = newValue;
		}
		setDiscreteMessage(copy);
	};

	const onChangeFormValueMessageId = (newValue: string) => {
		const copy: DiscreteToLn2EntryInterface = structuredClone(discreteMessage);
		copy.value.message.msg_id = newValue;
		setDiscreteMessage(copy);
	};

	const onChangeSelectBank = (id: string, name: string) => {
		const copy: DiscreteToLn2EntryInterface = structuredClone(discreteMessage);
		copy.value.bank_id.id = id;
		copy.value.bank_id.name = name;
		setDiscreteMessage(copy);
	};

	const onChangeSelectLift = (id: string, name: string) => {
		const copy: DiscreteToLn2EntryInterface = structuredClone(discreteMessage);
		copy.value.lift_id.id = id;
		copy.value.lift_id.name = name;
		setDiscreteMessage(copy);
	};

	const onClickAddNewDiscreteMessage = () => {
		const copy = structuredClone(cdoValue);
		copy.discrete_2_message.push(discreteMessage);
		setDiscreteMessage({
			key: {
				read_response_message: {
					board_address: 0,
					pin_num: 0,
					value: true,
				},
			},
			value: {
				message: {
					msg_id: "",
					active: true,
					position: 0,
				},
				lift_id: { id: "", name: "" },
				bank_id: { id: "", name: "" },
				dev_type: 0,
			},
		});
		setCdo(copy);
	};

	const onClickRemoveMessage = (index: number) => {
		const copy = structuredClone(cdoValue);
		copy.discrete_2_message.splice(index, 1);
		setCdo(copy);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<DrawerRowDivider icon={faFileCode} title='SEQUENT' />
			<Collapse>
				<Panel header={`Sequent JSON ${t("general.preview")}`} key='1'>
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<pre
								style={{
									width: "100%",
									color: "#dcdcdc",
									padding: "10px",
									borderRadius: "8px",
									fontSize: "14px",
									overflowX: "auto",
								}}
								dangerouslySetInnerHTML={{
									__html: formatJsonWithColors(cdoValue),
								}}
							/>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
			<Collapse>
				<Panel header={`Sequent ${t("general.formulary")}`} key='1'>
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										<span className='drawer__text'>
											{t("general.messages")}
										</span>
									</div>
									<div className='generalStyles__width100'>
										{cdoValue.discrete_2_message.map(
											(
												item: DiscreteToLn2EntryInterface,
												itemIndex: number,
											) => (
												<div
													key={itemIndex}
													className='mb-3'
													style={{
														backgroundColor: "#1f1f1f",
														borderRadius: "5px",
														fontSize: "9px",
														padding: "10px",
													}}
												>
													<div
														style={{
															width: "100%",
														}}
													>
														<pre
															style={{
																width: "100%",
																color: "#dcdcdc",
																padding: "10px",
																borderRadius: "8px",
																overflowX: "auto",
															}}
															dangerouslySetInnerHTML={{
																__html: formatJsonWithColors(item),
															}}
														/>
														<div className='generalStyles__flexCenter'>
															<Tooltip title={t("general.delete")}>
																<Button
																	onClick={() => {
																		onClickRemoveMessage(itemIndex);
																	}}
																	className='buttonStyle__29'
																	shape='circle'
																	icon={<FontAwesomeIcon icon={faTrash} />}
																/>
															</Tooltip>
														</div>
													</div>
												</div>
											),
										)}
									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
					</div>
					<br />
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<FormSelectDevType
								inputValue={cdoValue.dev_type}
								inputOnChange={onChangeSelect}
							/>
							<FormInputNumber
								inputValue={
									discreteMessage.key.read_response_message?.board_address || 0
								}
								inputLabelName={t("general.boardAddress")}
								inputOnChange={onChangeFormValue}
								inputVariableName='board_address'
								
							/>
							<FormInputNumber
								inputValue={
									discreteMessage.key.read_response_message?.pin_num || 0
								}
								inputLabelName={t("general.pinNumber")}
								inputOnChange={onChangeFormValue}
								inputVariableName='pin_num'
								
							/>
							<FormInputBooleanSwitch
								value={
									discreteMessage.key.read_response_message?.value || false
								}
								onChangeSwitch={onChangeFormValue}
								labelName={t("general.value")}
								variableName='value'
								isRequired
								disabledInput={false}
								
							/>
							<FormSelectMessage
								inputValue={discreteMessage.value.message.msg_id}
								inputOnChange={onChangeFormValueMessageId}
								disabledInput={false}
								displaySelect
							/>
							<FormInputBooleanSwitch
								value={discreteMessage.value.message.active}
								onChangeSwitch={onChangeFormValue}
								labelName={t("general.active")}
								variableName='active'
								isRequired
								disabledInput={false}
								
							/>
							<FormInputNumber
								inputValue={discreteMessage.value.message.position}
								inputLabelName={t("general.position")}
								inputOnChange={onChangeFormValue}
								inputVariableName='position'
								
							/>
							<FormSelectBank
								inputValue={discreteMessage.value.bank_id.id}
								inputOnChange={onChangeSelectBank}
								disabledInput={false}
								
							/>
							<FormSelectLift
								inputValue={discreteMessage.value.lift_id.id}
								inputOnChange={onChangeSelectLift}
								disabledInput={false}
							/>
							<FormSelectDevTypeNames
								inputValue={discreteMessage.value.dev_type}
								inputOnChange={onChangeSelectMessageDevType}
							/>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired' />
									</div>
									<div className='generalStyles__width100'>
										<Button
											onClick={onClickAddNewDiscreteMessage}
											className='buttonStyle__28 generalStyles__width100'
										>
											<FontAwesomeIcon
												icon={faPlus}
												className='generalStyles__mrFix'
											/>
											{t("general.add")}
										</Button>
									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
		</div>
	);
}
